import React, { useState, useEffect, useRef } from 'react';
import { PlusIcon, DocumentTextIcon, PrinterIcon } from '@heroicons/react/24/outline';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import DataTable from '../components/common/DataTable';
import { payrollService } from '../services/hrService';
import { formatDate, formatCurrency } from '../utils/format';

export default function Payroll() {
  const [periods, setPeriods] = useState([]);
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('periods'); // 'periods' or 'payrolls'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [periodPayrolls, setPeriodPayrolls] = useState([]);
  const [isPayslipModalOpen, setIsPayslipModalOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const printRef = useRef();
  const payslipRef = useRef();
  const [formData, setFormData] = useState({
    period_name: '',
    start_date: '',
    end_date: '',
    payment_date: ''
  });

  useEffect(() => {
    if (activeTab === 'periods') {
      fetchPeriods();
    } else {
      fetchPayrolls();
    }
  }, [activeTab]);

  const fetchPeriods = async () => {
    try {
      setLoading(true);
      const response = await payrollService.getPeriods();
      setPeriods(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching payroll periods:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const response = await payrollService.getPayrolls();
      setPayrolls(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (period = null) => {
    if (period) {
      setSelectedPeriod(period);
      setFormData({
        period_name: period.period_name,
        start_date: period.start_date,
        end_date: period.end_date,
        payment_date: period.payment_date
      });
    } else {
      setSelectedPeriod(null);
      setFormData({
        period_name: '',
        start_date: '',
        end_date: '',
        payment_date: ''
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPeriod(null);
    setFormData({
      period_name: '',
      start_date: '',
      end_date: '',
      payment_date: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedPeriod) {
        await payrollService.updatePeriod(selectedPeriod.id, formData);
      } else {
        await payrollService.createPeriod(formData);
      }
      closeModal();
      fetchPeriods();
    } catch (error) {
      console.error('Error saving payroll period:', error);
      alert(error.response?.data?.detail || 'Failed to save payroll period');
    }
  };

  const handleProcessPeriod = async (periodId) => {
    if (window.confirm('Are you sure you want to process this payroll period? This will calculate payroll for all active employees.')) {
      try {
        await payrollService.processPeriod(periodId);
        alert('Payroll period processed successfully!');
        fetchPeriods();
        fetchPayrolls();
      } catch (error) {
        console.error('Error processing payroll period:', error);
        alert(error.response?.data?.detail || 'Failed to process payroll period');
      }
    }
  };

  const handleRevertPeriod = async (periodId) => {
    if (window.confirm('Are you sure you want to revert this payroll period? This will delete all payroll records for this period and reset it to draft status.')) {
      try {
        await payrollService.revertPeriod(periodId);
        alert('Payroll period reverted successfully! You can now reprocess it.');
        fetchPeriods();
        fetchPayrolls();
        closeDetailsModal();
      } catch (error) {
        console.error('Error reverting payroll period:', error);
        alert(error.response?.data?.detail || 'Failed to revert payroll period');
      }
    }
  };

  const handlePayPeriod = async (periodId) => {
    if (window.confirm('Are you sure you want to mark this payroll as PAID? This will apply all loan and cash advance deductions and cannot be reverted.')) {
      try {
        await payrollService.payPeriod(periodId);
        alert('Payroll period marked as PAID! Loan and cash advance deductions have been applied.');
        fetchPeriods();
        fetchPayrolls();
        closeDetailsModal();
      } catch (error) {
        console.error('Error marking payroll as paid:', error);
        alert(error.response?.data?.detail || 'Failed to mark payroll as paid');
      }
    }
  };

  const handleViewDetails = async (periodId) => {
    try {
      setLoading(true);
      const period = periods.find(p => p.id === periodId);
      setSelectedPeriod(period);

      // Fetch payrolls for this period
      const response = await payrollService.getPayrolls({ period: periodId });
      setPeriodPayrolls(response.data.results || response.data);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error('Error fetching period details:', error);
      alert('Failed to load payroll details');
    } finally {
      setLoading(false);
    }
  };

  const closeDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedPeriod(null);
    setPeriodPayrolls([]);
  };

  const handlePrint = () => {
    const printContent = printRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payroll Register - ${selectedPeriod?.period_name || 'Report'}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #000; padding: 6px 4px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .text-right { text-align: right; }
            .text-center { text-align: center; }
            .font-bold { font-weight: bold; }
            .header { text-align: center; margin-bottom: 15px; border-bottom: 2px solid #000; padding-bottom: 10px; }
            .company-name { font-size: 18px; font-weight: bold; }
            .company-address { font-size: 10px; color: #333; margin-top: 3px; }
            .report-title { font-size: 14px; margin-top: 8px; font-weight: bold; }
            .period-info { margin: 15px 0; padding: 8px 10px; border: 1px solid #ccc; font-size: 11px; display: flex; justify-content: space-between; }
            .period-info span { margin-right: 20px; }
            .dole-notice { margin-top: 0; font-size: 9px; color: #666; padding: 3px 0 0 0; }
            .dole-notice p { margin: 0; padding: 0; }
            .signatures { margin-top: 40px; display: flex; justify-content: space-between; }
            .signature-box { width: 200px; text-align: center; }
            .signature-line { border-top: 1px solid #000; padding-top: 5px; }
            .digital-sign { font-size: 9px; color: #666; font-style: italic; margin-bottom: 3px; }
            .signatory-name { font-weight: bold; font-size: 11px; margin-bottom: 3px; }
            .signatory-title { font-size: 10px; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              @page { size: landscape; margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="company-name">FIBERFLOW TECHNOLOGIES INC.</div>
            <div class="company-address">Odiongan, Romblon, Philippines</div>
            <div class="company-address">TIN: 670-207-920-00000</div>
            <div class="report-title">PAYROLL REGISTER</div>
          </div>
          <div class="period-info">
            <span><strong>Pay Period:</strong> ${selectedPeriod ? selectedPeriod.start_date + ' to ' + selectedPeriod.end_date : ''}</span>
            <span><strong>Pay Date:</strong> ${selectedPeriod ? selectedPeriod.payment_date : ''}</span>
            <span><strong>Total Employees:</strong> ${periodPayrolls.length}</span>
          </div>
          ${printContent.innerHTML}
          <div class="dole-notice">
            <p><strong>DOLE Compliance Notice:</strong> This payroll register is prepared in accordance with the Labor Code of the Philippines and DOLE Labor Advisory No. 06, Series of 2020. All amounts are in Philippine Peso (₱).</p>
            <p>Statutory Deductions: SSS (RA 11199), PhilHealth (RA 11223), Pag-IBIG (RA 9679), Withholding Tax (NIRC)</p>
          </div>
          <div class="signatures">
            <div class="signature-box">
              <div class="digital-sign">(Digitally Signed - System Generated)</div>
              <div class="signatory-name">${selectedPeriod?.processed_by_name || '—'}</div>
              <div class="signature-line">Prepared By</div>
            </div>
            <div class="signature-box">
              <div class="digital-sign">(Digitally Signed - System Generated)</div>
              <div class="signatory-name">${selectedPeriod?.checked_by_name || '—'}</div>
              <div class="signature-line">Checked By</div>
            </div>
            <div class="signature-box">
              <div class="digital-sign">(Digitally Signed - System Generated)</div>
              <div class="signatory-name">${selectedPeriod?.approved_by_name || '—'}</div>
              <div class="signature-line">Approved By</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleViewPayslip = (payroll) => {
    setSelectedPayroll(payroll);
    setIsPayslipModalOpen(true);
  };

  const closePayslipModal = () => {
    setIsPayslipModalOpen(false);
    setSelectedPayroll(null);
  };

  const formatCompactPeriod = (startDate, endDate) => {
    if (!startDate || !endDate) return '';
    const s = new Date(startDate + 'T00:00:00');
    const e = new Date(endDate + 'T00:00:00');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
      return `${months[s.getMonth()]} ${String(s.getDate()).padStart(2,'0')} - ${String(e.getDate()).padStart(2,'0')}, ${e.getFullYear()}`;
    }
    return `${months[s.getMonth()]} ${String(s.getDate()).padStart(2,'0')} - ${months[e.getMonth()]} ${String(e.getDate()).padStart(2,'0')}, ${e.getFullYear()}`;
  };

  const formatShortDate = (date) => {
    if (!date) return '';
    const d = new Date(date + 'T00:00:00');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[d.getMonth()]} ${String(d.getDate()).padStart(2,'0')}, ${d.getFullYear()}`;
  };

  const handlePrintPayslip = () => {
    const printContent = payslipRef.current;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Payslip - ${selectedPayroll?.employee_name || 'Employee'}</title>
          <style>
            @page {
              size: 4.25in 5.5in;
              margin: 0;
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              font-size: 9px;
              width: 4.25in;
              height: 5.5in;
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .payslip {
              border: 2px solid #1e3a5f;
              width: 4.25in;
              height: 5.5in;
              display: flex;
              flex-direction: column;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #1e3a5f;
              padding: 8px 8px;
              background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
            }
            .company-name { font-size: 14px; font-weight: bold; color: #1e3a5f; letter-spacing: 0.5px; }
            .company-address { font-size: 8px; color: #555; margin-top: 2px; }
            .payslip-title { font-size: 12px; margin-top: 4px; font-weight: bold; color: #333; letter-spacing: 1px; }

            .employee-section {
              display: grid;
              grid-template-columns: 2fr 0.8fr 1.1fr 1.1fr;
              border-bottom: 1px solid #1e3a5f;
              background: #fff;
            }
            .employee-section > div {
              padding: 6px 5px;
              border-right: 1px solid #dee2e6;
            }
            .employee-section > div:last-child { border-right: none; }
            .employee-section .label { white-space: nowrap; }
            .employee-section .value { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 8px; }

            .period-section {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              border-bottom: 1px solid #1e3a5f;
              background: #e3f2fd;
            }
            .period-section > div {
              padding: 5px 5px;
              border-right: 1px solid #90caf9;
            }
            .period-section > div:last-child { border-right: none; }
            .period-section .value { white-space: nowrap; font-size: 8px; }

            .label { font-size: 6px; color: #666; text-transform: uppercase; font-weight: 500; }
            .value { font-weight: bold; margin-top: 1px; font-size: 8px; }

            .earnings-deductions {
              display: grid;
              grid-template-columns: 1fr 1fr;
              flex: 1;
            }
            .earnings { border-right: 2px solid #1e3a5f; display: flex; flex-direction: column; }
            .deductions { display: flex; flex-direction: column; }
            .section-header {
              background: #e9ecef;
              padding: 6px 8px;
              font-weight: bold;
              font-size: 10px;
              border-bottom: 1px solid #dee2e6;
              color: #333;
            }
            .items-container { flex: 1; display: flex; flex-direction: column; }
            .line-item {
              display: flex;
              justify-content: space-between;
              padding: 4px 8px;
              border-bottom: 1px solid #f0f0f0;
              align-items: flex-start;
            }
            .line-item .desc { font-size: 8px; font-weight: 500; }
            .line-item .legal { font-size: 6px; color: #888; display: block; margin-top: 1px; }
            .line-item .amount { font-size: 9px; font-weight: 600; text-align: right; white-space: nowrap; }
            .line-item .amount.deduction { color: #c62828; }
            .subtotal {
              background: #f5f5f5;
              font-weight: bold;
              padding: 6px 8px;
              margin-top: auto;
            }
            .subtotal .desc { font-size: 9px; }
            .subtotal .amount { font-size: 10px; }
            .subtotal.deduction { background: #ffebee; color: #c62828; }

            .net-pay-section {
              background: linear-gradient(to right, #c8e6c9, #a5d6a7);
              padding: 10px 12px;
              border-top: 2px solid #1e3a5f;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .net-pay-label { font-size: 14px; font-weight: bold; color: #1b5e20; }
            .net-pay-sublabel { font-size: 7px; color: #2e7d32; margin-top: 2px; }
            .net-pay-amount { font-size: 22px; font-weight: bold; color: #1b5e20; }

            .footer {
              padding: 6px 8px;
              font-size: 6px;
              color: #666;
              background: #fafafa;
              border-top: 1px solid #dee2e6;
            }
            .footer p { margin: 1px 0; line-height: 1.3; }
            .footer .title { font-weight: bold; color: #333; }
          </style>
        </head>
        <body>
          <div class="payslip">
            <div class="header">
              <div class="company-name">FIBERFLOW TECHNOLOGIES INC.</div>
              <div class="company-address">Odiongan, Romblon, Philippines</div>
              <div class="company-address">TIN: 670-207-920-00000</div>
              <div class="payslip-title">PAYSLIP</div>
            </div>

            <div class="employee-section">
              <div>
                <div class="label">Employee Name</div>
                <div class="value">${selectedPayroll?.employee_name || ''}</div>
              </div>
              <div>
                <div class="label">Emp. No.</div>
                <div class="value">${selectedPayroll?.employee_no || ''}</div>
              </div>
              <div>
                <div class="label">Department</div>
                <div class="value">${selectedPayroll?.employee?.department?.name || 'N/A'}</div>
              </div>
              <div>
                <div class="label">Position</div>
                <div class="value">${selectedPayroll?.employee?.position?.title || 'N/A'}</div>
              </div>
            </div>

            <div class="period-section">
              <div>
                <div class="label">Pay Period</div>
                <div class="value">${selectedPeriod ? formatCompactPeriod(selectedPeriod.start_date, selectedPeriod.end_date) : ''}</div>
              </div>
              <div>
                <div class="label">Pay Date</div>
                <div class="value">${selectedPeriod ? formatShortDate(selectedPeriod.payment_date) : ''}</div>
              </div>
              <div>
                <div class="label">Days Worked / Hours</div>
                <div class="value">${selectedPayroll?.days_worked || 0} days / ${selectedPayroll?.hours_worked || (parseFloat(selectedPayroll?.days_worked || 0) * 8).toFixed(2)} hrs</div>
              </div>
            </div>

            <div class="earnings-deductions">
              <div class="earnings">
                <div class="section-header">EARNINGS</div>
                <div class="items-container">
                  <div class="line-item">
                    <div><span class="desc">Basic Pay</span><span class="legal">(${selectedPayroll?.days_worked || 0} days @ daily rate)</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.basic_pay || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Overtime Pay</span><span class="legal">(125% of hourly rate - Art. 87)</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.overtime_pay || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Holiday Pay</span><span class="legal">(Art. 94, Labor Code)</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.holiday_pay || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Night Shift Differential</span><span class="legal">(10% premium - Art. 86)</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.night_differential || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Allowances</span><span class="legal">(Non-taxable benefits)</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.allowances || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Other Earnings</span></div>
                    <span class="amount">${formatCurrency(selectedPayroll?.other_earnings || 0)}</span>
                  </div>
                  <div class="line-item subtotal">
                    <span class="desc">GROSS PAY</span>
                    <span class="amount">${formatCurrency(selectedPayroll?.gross_pay || 0)}</span>
                  </div>
                </div>
              </div>

              <div class="deductions">
                <div class="section-header">DEDUCTIONS</div>
                <div class="items-container">
                  <div class="line-item">
                    <div><span class="desc">SSS Contribution</span><span class="legal">(RA 11199 - Social Security)</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.sss_contribution || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">PhilHealth</span><span class="legal">(RA 11223 - Universal Health)</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.philhealth_contribution || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Pag-IBIG/HDMF</span><span class="legal">(RA 9679 - Home Dev Fund)</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.pagibig_contribution || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Withholding Tax</span><span class="legal">(TRAIN Law - RA 10963)</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.withholding_tax || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">Loans</span><span class="legal">(Authorized deduction)</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.loans || 0)}</span>
                  </div>
                  <div class="line-item">
                    <div><span class="desc">CA/Other Deductions</span></div>
                    <span class="amount deduction">${formatCurrency(selectedPayroll?.other_deductions || 0)}</span>
                  </div>
                  <div class="line-item subtotal deduction">
                    <span class="desc">TOTAL DEDUCTIONS</span>
                    <span class="amount">${formatCurrency(selectedPayroll?.total_deductions || 0)}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="net-pay-section">
              <div>
                <div class="net-pay-label">NET PAY</div>
                <div class="net-pay-sublabel">Amount to be received</div>
              </div>
              <div class="net-pay-amount">₱${parseFloat(selectedPayroll?.net_pay || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </div>

            <div class="footer">
              <p class="title">DOLE COMPLIANCE NOTICE (Labor Advisory No. 06-20):</p>
              <p>This payslip is issued in compliance with Article 103 of the Labor Code of the Philippines. Statutory deductions are made pursuant to applicable laws. Employee has the right to question any deduction within 10 days of receipt.</p>
              <p style="margin-top: 3px;">For inquiries: HR Department | This is a computer-generated document.</p>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const handlePrintAllPayslips = () => {
    if (!periodPayrolls || periodPayrolls.length === 0) {
      alert('No payroll records to print');
      return;
    }

    const printWindow = window.open('', '_blank');

    const generatePayslipHTML = (payroll) => `
      <div class="payslip">
        <div class="header">
          <div class="company-name">FIBERFLOW TECHNOLOGIES INC.</div>
          <div class="company-address">Odiongan, Romblon, Philippines</div>
          <div class="company-address">TIN: 670-207-920-00000</div>
          <div class="payslip-title">PAYSLIP</div>
        </div>

        <div class="employee-section">
          <div>
            <div class="label">Employee Name</div>
            <div class="value">${payroll.employee_name || ''}</div>
          </div>
          <div>
            <div class="label">Emp. No.</div>
            <div class="value">${payroll.employee_no || ''}</div>
          </div>
          <div>
            <div class="label">Department</div>
            <div class="value">${payroll.employee?.department?.name || 'N/A'}</div>
          </div>
          <div>
            <div class="label">Position</div>
            <div class="value">${payroll.employee?.position?.title || 'N/A'}</div>
          </div>
        </div>

        <div class="period-section">
          <div>
            <div class="label">Pay Period</div>
            <div class="value">${selectedPeriod ? formatCompactPeriod(selectedPeriod.start_date, selectedPeriod.end_date) : ''}</div>
          </div>
          <div>
            <div class="label">Pay Date</div>
            <div class="value">${selectedPeriod ? formatShortDate(selectedPeriod.payment_date) : ''}</div>
          </div>
          <div>
            <div class="label">Days Worked / Hours</div>
            <div class="value">${payroll.days_worked || 0} days / ${payroll.hours_worked || (parseFloat(payroll.days_worked || 0) * 8).toFixed(2)} hrs</div>
          </div>
        </div>

        <div class="earnings-deductions">
          <div class="earnings">
            <div class="section-header">EARNINGS</div>
            <div class="items-container">
              <div class="line-item">
                <div><span class="desc">Basic Pay</span><span class="legal">(${payroll.days_worked || 0} days @ daily rate)</span></div>
                <span class="amount">${formatCurrency(payroll.basic_pay || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Overtime Pay</span><span class="legal">(125% of hourly rate - Art. 87)</span></div>
                <span class="amount">${formatCurrency(payroll.overtime_pay || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Holiday Pay</span><span class="legal">(Art. 94, Labor Code)</span></div>
                <span class="amount">${formatCurrency(payroll.holiday_pay || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Night Shift Differential</span><span class="legal">(10% premium - Art. 86)</span></div>
                <span class="amount">${formatCurrency(payroll.night_differential || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Allowances</span><span class="legal">(Non-taxable benefits)</span></div>
                <span class="amount">${formatCurrency(payroll.allowances || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Other Earnings</span></div>
                <span class="amount">${formatCurrency(payroll.other_earnings || 0)}</span>
              </div>
              <div class="line-item subtotal">
                <span class="desc">GROSS PAY</span>
                <span class="amount">${formatCurrency(payroll.gross_pay || 0)}</span>
              </div>
            </div>
          </div>

          <div class="deductions">
            <div class="section-header">DEDUCTIONS</div>
            <div class="items-container">
              <div class="line-item">
                <div><span class="desc">SSS Contribution</span><span class="legal">(RA 11199 - Social Security)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.sss_contribution || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">PhilHealth</span><span class="legal">(RA 11223 - Universal Health)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.philhealth_contribution || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Pag-IBIG/HDMF</span><span class="legal">(RA 9679 - Home Dev Fund)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.pagibig_contribution || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Withholding Tax</span><span class="legal">(TRAIN Law - RA 10963)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.withholding_tax || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Late Deduction</span><span class="legal">(Actual late hours × hourly rate)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.late_deduction || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">Loans</span><span class="legal">(Authorized deduction)</span></div>
                <span class="amount deduction">${formatCurrency(payroll.loans || 0)}</span>
              </div>
              <div class="line-item">
                <div><span class="desc">CA/Other Deductions</span></div>
                <span class="amount deduction">${formatCurrency(payroll.other_deductions || 0)}</span>
              </div>
              <div class="line-item subtotal deduction">
                <span class="desc">TOTAL DEDUCTIONS</span>
                <span class="amount">${formatCurrency(payroll.total_deductions || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="net-pay-section">
          <div>
            <div class="net-pay-label">NET PAY</div>
            <div class="net-pay-sublabel">Amount to be received</div>
          </div>
          <div class="net-pay-amount">₱${parseFloat(payroll.net_pay || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
        </div>

        <div class="footer">
          <p class="title">DOLE COMPLIANCE NOTICE (Labor Advisory No. 06-20):</p>
          <p>This payslip is issued in compliance with Article 103 of the Labor Code of the Philippines. Statutory deductions are made pursuant to applicable laws. Employee has the right to question any deduction within 10 days of receipt.</p>
          <p style="margin-top: 3px;">For inquiries: HR Department | This is a computer-generated document.</p>
        </div>
      </div>
    `;

    // Group payslips into pages of 4
    const payslipPages = [];
    for (let i = 0; i < periodPayrolls.length; i += 4) {
      const pagePayrolls = periodPayrolls.slice(i, i + 4);
      const pagePayslips = pagePayrolls.map(payroll => generatePayslipHTML(payroll)).join('');
      payslipPages.push(`<div class="page"><div class="page-grid">${pagePayslips}</div></div>`);
    }
    const allPages = payslipPages.join('');

    printWindow.document.write(`
      <html>
        <head>
          <title>All Payslips - ${selectedPeriod?.period_name || 'Payroll'}</title>
          <style>
            @page {
              size: letter;
              margin: 0;
            }
            @media print {
              html, body { height: auto; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body {
              font-family: Arial, sans-serif;
              font-size: 7px;
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .page {
              width: 8.5in;
              height: 10.5in;
              padding: 0.25in;
              page-break-after: always;
              page-break-inside: avoid;
              break-after: page;
            }
            .page:last-child { page-break-after: auto; break-after: auto; }
            .page-grid {
              display: grid;
              grid-template-columns: repeat(2, 4in);
              grid-template-rows: repeat(2, 5in);
              width: 8in;
              height: 10in;
            }
            .payslip {
              border: 1px solid #1e3a5f;
              width: 4in;
              height: 5in;
              display: flex;
              flex-direction: column;
              overflow: hidden;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #1e3a5f;
              padding: 10px 8px;
              background: linear-gradient(to bottom, #f8f9fa, #e9ecef);
            }
            .company-name { font-size: 14px; font-weight: bold; color: #1e3a5f; letter-spacing: 0.5px; }
            .company-address { font-size: 8px; color: #555; margin-top: 2px; }
            .payslip-title { font-size: 12px; margin-top: 6px; font-weight: bold; color: #333; letter-spacing: 1px; }

            .employee-section {
              display: grid;
              grid-template-columns: 2fr 0.8fr 1.1fr 1.1fr;
              border-bottom: 1px solid #1e3a5f;
              background: #fff;
            }
            .employee-section > div {
              padding: 6px 5px;
              border-right: 1px solid #dee2e6;
            }
            .employee-section > div:last-child { border-right: none; }
            .employee-section .label { white-space: nowrap; }
            .employee-section .value { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-size: 8px; }

            .period-section {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              border-bottom: 1px solid #1e3a5f;
              background: #e3f2fd;
            }
            .period-section > div {
              padding: 5px 5px;
              border-right: 1px solid #90caf9;
            }
            .period-section > div:last-child { border-right: none; }
            .period-section .value { white-space: nowrap; font-size: 8px; }

            .label { font-size: 6px; color: #666; text-transform: uppercase; font-weight: 500; }
            .value { font-weight: bold; margin-top: 1px; font-size: 8px; }

            .earnings-deductions {
              display: grid;
              grid-template-columns: 1fr 1fr;
              flex: 1;
            }
            .earnings { border-right: 2px solid #1e3a5f; display: flex; flex-direction: column; }
            .deductions { display: flex; flex-direction: column; }
            .section-header {
              background: #e9ecef;
              padding: 6px 8px;
              font-weight: bold;
              font-size: 10px;
              border-bottom: 1px solid #dee2e6;
              color: #333;
            }
            .items-container { flex: 1; display: flex; flex-direction: column; }
            .line-item {
              display: flex;
              justify-content: space-between;
              padding: 4px 8px;
              border-bottom: 1px solid #f0f0f0;
              align-items: flex-start;
            }
            .line-item .desc { font-size: 8px; font-weight: 500; }
            .line-item .legal { font-size: 6px; color: #888; display: block; margin-top: 1px; }
            .line-item .amount { font-size: 9px; font-weight: 600; text-align: right; white-space: nowrap; }
            .line-item .amount.deduction { color: #c62828; }
            .subtotal {
              background: #f5f5f5;
              font-weight: bold;
              padding: 6px 8px;
              margin-top: auto;
            }
            .subtotal .desc { font-size: 9px; }
            .subtotal .amount { font-size: 10px; }
            .subtotal.deduction { background: #ffebee; color: #c62828; }

            .net-pay-section {
              background: linear-gradient(to right, #c8e6c9, #a5d6a7);
              padding: 10px 12px;
              border-top: 2px solid #1e3a5f;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            .net-pay-label { font-size: 14px; font-weight: bold; color: #1b5e20; }
            .net-pay-sublabel { font-size: 7px; color: #2e7d32; margin-top: 2px; }
            .net-pay-amount { font-size: 22px; font-weight: bold; color: #1b5e20; }

            .footer {
              padding: 6px 8px;
              font-size: 6px;
              color: #666;
              background: #fafafa;
              border-top: 1px solid #dee2e6;
            }
            .footer p { margin: 1px 0; line-height: 1.3; }
            .footer .title { font-weight: bold; color: #333; }
          </style>
        </head>
        <body>
          ${allPages}
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      processing: 'bg-blue-100 text-blue-800',
      processed: 'bg-green-100 text-green-800',
      paid: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const periodColumns = [
    {
      key: 'name',
      label: 'Period Name',
      render: (period) => period.period_name || '-'
    },
    {
      key: 'start_date',
      label: 'Start Date',
      render: (period) => formatDate(period.start_date)
    },
    {
      key: 'end_date',
      label: 'End Date',
      render: (period) => formatDate(period.end_date)
    },
    {
      key: 'pay_date',
      label: 'Pay Date',
      render: (period) => formatDate(period.payment_date)
    },
    {
      key: 'status',
      label: 'Status',
      render: (period) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(period.status)}`}>
          {(period.status || 'draft').toUpperCase()}
        </span>
      ),
    },
    {
      key: 'total_amount',
      label: 'Total Amount',
      render: (period) => period.total_net_pay ? formatCurrency(period.total_net_pay) : '-'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (period) => (
        <div className="flex space-x-2">
          {period.status === 'draft' && (
            <Button
              size="sm"
              onClick={() => handleProcessPeriod(period.id)}
            >
              Process
            </Button>
          )}
          {period.status === 'processed' && (
            <>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleViewDetails(period.id)}
              >
                View
              </Button>
              <Button
                size="sm"
                variant="danger"
                onClick={() => handleRevertPeriod(period.id)}
              >
                Revert
              </Button>
              <Button
                size="sm"
                variant="success"
                onClick={() => handlePayPeriod(period.id)}
              >
                Mark Paid
              </Button>
            </>
          )}
          {period.status === 'paid' && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => handleViewDetails(period.id)}
            >
              View Details
            </Button>
          )}
        </div>
      ),
    },
  ];

  const payrollColumns = [
    {
      key: 'employee',
      label: 'Employee',
      render: (payroll) => payroll.employee ? `${payroll.employee.first_name} ${payroll.employee.last_name}` : '-'
    },
    {
      key: 'period',
      label: 'Period',
      render: (payroll) => payroll.period_name || payroll.period?.period_name || '-'
    },
    {
      key: 'basic_salary',
      label: 'Basic Salary',
      render: (payroll) => formatCurrency(payroll.basic_pay)
    },
    {
      key: 'allowances',
      label: 'Allowances',
      render: (payroll) => formatCurrency(payroll.allowances)
    },
    {
      key: 'deductions',
      label: 'Deductions',
      render: (payroll) => formatCurrency(payroll.total_deductions)
    },
    {
      key: 'net_pay',
      label: 'Net Pay',
      render: (payroll) => (
        <span className="font-semibold text-green-600">
          {formatCurrency(payroll.net_pay)}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (payroll) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payroll.status)}`}>
          {(payroll.status || 'draft').toUpperCase()}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (payroll) => (
        <button
          onClick={() => handleViewPayslip(payroll)}
          className="text-blue-600 hover:text-blue-900"
          title="View Payslip"
        >
          <DocumentTextIcon className="h-5 w-5" />
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Payroll Management</h1>
        {activeTab === 'periods' && (
          <Button onClick={() => openModal()}>
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Period
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('periods')}
            className={`${
              activeTab === 'periods'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Payroll Periods
          </button>
          <button
            onClick={() => setActiveTab('payrolls')}
            className={`${
              activeTab === 'payrolls'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Employee Payrolls
          </button>
        </nav>
      </div>

      {/* Payroll Summary Stats */}
      {activeTab === 'payrolls' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Employees</h3>
            <p className="mt-2 text-3xl font-semibold text-gray-900">
              {payrolls.length}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Basic Salary</h3>
            <p className="mt-2 text-2xl font-semibold text-blue-600">
              {formatCurrency(payrolls.reduce((sum, p) => sum + parseFloat(p.basic_pay || 0), 0))}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Deductions</h3>
            <p className="mt-2 text-2xl font-semibold text-red-600">
              {formatCurrency(payrolls.reduce((sum, p) => sum + parseFloat(p.total_deductions || 0), 0))}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Net Payout</h3>
            <p className="mt-2 text-2xl font-semibold text-green-600">
              {formatCurrency(payrolls.reduce((sum, p) => sum + parseFloat(p.net_pay || 0), 0))}
            </p>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="bg-white shadow rounded-lg">
        <DataTable
          columns={activeTab === 'periods' ? periodColumns : payrollColumns}
          data={activeTab === 'periods' ? periods : payrolls}
          loading={loading}
        />
      </div>

      {/* Create/Edit Period Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedPeriod ? 'Edit Payroll Period' : 'Create Payroll Period'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Period Name *
            </label>
            <input
              type="text"
              required
              value={formData.period_name}
              onChange={(e) => setFormData({ ...formData, period_name: e.target.value })}
              placeholder="e.g., January 2026 - First Half"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Start Date *
              </label>
              <input
                type="date"
                required
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                End Date *
              </label>
              <input
                type="date"
                required
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Pay Date *
            </label>
            <input
              type="date"
              required
              value={formData.payment_date}
              onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div className="bg-blue-50 p-4 rounded-md">
            <p className="text-sm text-blue-800">
              After creating the period, you can process it to automatically calculate payroll for all active employees based on their attendance and leave records.
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button type="submit">
              {selectedPeriod ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Payroll Details Modal */}
      <Modal
        isOpen={isDetailsModalOpen}
        onClose={closeDetailsModal}
        title={selectedPeriod ? `Payroll Details - ${selectedPeriod.period_name}` : 'Payroll Details'}
        size="full"
      >
        <div className="space-y-4">
          {selectedPeriod && (
            <div className="bg-gray-50 p-4 rounded-lg grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Period:</span>
                <p className="font-medium">{formatDate(selectedPeriod.start_date)} - {formatDate(selectedPeriod.end_date)}</p>
              </div>
              <div>
                <span className="text-gray-500">Pay Date:</span>
                <p className="font-medium">{formatDate(selectedPeriod.payment_date)}</p>
              </div>
              <div>
                <span className="text-gray-500">Employees:</span>
                <p className="font-medium">{periodPayrolls.length}</p>
              </div>
              <div>
                <span className="text-gray-500">Total Payout:</span>
                <p className="font-medium text-green-600">{formatCurrency(selectedPeriod.total_net_pay)}</p>
              </div>
            </div>
          )}

          <div ref={printRef}>
            <table className="w-full divide-y divide-gray-200 text-xs">
              <thead className="bg-gray-100">
                <tr>
                  <th rowSpan="2" className="px-2 py-2 text-left text-xs font-bold text-gray-700 uppercase border-b border-r border-gray-300 bg-gray-100">Employee</th>
                  <th colSpan="5" className="px-2 py-1 text-center text-xs font-bold text-gray-700 uppercase border-b border-r border-gray-300 bg-blue-50">Work & Earnings</th>
                  <th colSpan="7" className="px-2 py-1 text-center text-xs font-bold text-gray-700 uppercase border-b border-r border-gray-300 bg-red-50">Deductions</th>
                  <th rowSpan="2" className="px-2 py-2 text-right text-xs font-bold text-gray-700 uppercase border-b border-gray-300 bg-green-50">Net Pay</th>
                </tr>
                <tr className="bg-gray-50">
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Days</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Hours</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Basic</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">OT Pay</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200 bg-blue-50">Gross</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">SSS</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">PhilHealth</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Pag-IBIG</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Tax</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">Loans</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200">CA/Other</th>
                  <th className="px-2 py-1 text-right text-[10px] font-medium text-gray-600 border-b border-r border-gray-200 bg-red-50">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {periodPayrolls.map((payroll, index) => (
                  <tr key={payroll.id} className={`hover:bg-blue-50 cursor-pointer ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} onClick={() => handleViewPayslip(payroll)}>
                    <td className="px-2 py-2 whitespace-nowrap border-r border-gray-200 bg-inherit">
                      <div className="text-xs font-medium text-gray-900">
                        {payroll.employee_name || payroll.employee?.first_name + ' ' + payroll.employee?.last_name || `#${payroll.employee_no}`}
                      </div>
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap text-right border-r border-gray-200">{payroll.days_worked}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right border-r border-gray-200">{payroll.hours_worked || (parseFloat(payroll.days_worked) * 8).toFixed(0)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right border-r border-gray-200">{formatCurrency(payroll.basic_pay || payroll.basic_salary)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right border-r border-gray-200">{formatCurrency(payroll.overtime_pay || 0)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right font-medium border-r border-gray-200 bg-blue-50">{formatCurrency(payroll.gross_pay)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.sss_contribution)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.philhealth_contribution)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.pagibig_contribution)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.withholding_tax)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.loans || 0)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-600 border-r border-gray-200">{formatCurrency(payroll.other_deductions || 0)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right text-red-700 font-medium border-r border-gray-200 bg-red-50">{formatCurrency(payroll.total_deductions)}</td>
                    <td className="px-2 py-2 whitespace-nowrap text-right font-bold text-green-700 bg-green-50">{formatCurrency(payroll.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-100 font-bold">
                <tr>
                  <td className="px-2 py-2 text-xs uppercase border-t-2 border-gray-400">TOTAL</td>
                  <td className="px-2 py-2 text-right border-t-2 border-gray-400">
                    {periodPayrolls.reduce((sum, p) => sum + parseFloat(p.days_worked || 0), 0).toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-right border-t-2 border-gray-400">
                    {periodPayrolls.reduce((sum, p) => sum + parseFloat(p.hours_worked || parseFloat(p.days_worked) * 8 || 0), 0).toFixed(0)}
                  </td>
                  <td className="px-2 py-2 text-right border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.basic_pay || p.basic_salary || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.overtime_pay || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right border-t-2 border-gray-400 bg-blue-50">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.gross_pay || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.sss_contribution || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.philhealth_contribution || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.pagibig_contribution || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.withholding_tax || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.loans || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.other_deductions || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-red-700 border-t-2 border-gray-400 bg-red-50">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.total_deductions || 0), 0))}
                  </td>
                  <td className="px-2 py-2 text-right text-green-700 border-t-2 border-gray-400 bg-green-50">
                    {formatCurrency(periodPayrolls.reduce((sum, p) => sum + parseFloat(p.net_pay || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {/* DOLE Compliance Note - Screen only */}
          <div className="mt-3 p-2 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-600 print:hidden">
            <p><strong>DOLE Compliance:</strong> This payroll register is prepared pursuant to DOLE D.O. 183-17 and Labor Advisory No. 06-20.</p>
            <p><strong>Statutory Deductions:</strong> SSS (RA 11199) | PhilHealth (RA 11223) | Pag-IBIG (RA 9679) | Withholding Tax (TRAIN Law RA 10963)</p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="secondary" onClick={handlePrint}>
              <PrinterIcon className="h-5 w-5 mr-2" />
              Print Report
            </Button>
            <Button variant="primary" onClick={handlePrintAllPayslips}>
              <PrinterIcon className="h-5 w-5 mr-2" />
              Print All Payslips
            </Button>
            {selectedPeriod?.status === 'processed' && (
              <>
                <Button variant="danger" onClick={() => handleRevertPeriod(selectedPeriod.id)}>
                  Revert to Draft
                </Button>
                <Button variant="success" onClick={() => handlePayPeriod(selectedPeriod.id)}>
                  Mark as Paid
                </Button>
              </>
            )}
            <Button variant="secondary" onClick={closeDetailsModal}>
              Close
            </Button>
          </div>
        </div>
      </Modal>

      {/* Payslip Modal - DOLE Compliant */}
      <Modal
        isOpen={isPayslipModalOpen}
        onClose={closePayslipModal}
        title="Employee Payslip (DOLE Compliant)"
        size="lg"
      >
        {selectedPayroll && (
          <div ref={payslipRef}>
            <div className="payslip border-2 border-gray-800">
              {/* Company Header - DOLE Required */}
              <div className="text-center border-b-2 border-gray-800 p-4 bg-gray-100">
                <h2 className="text-xl font-bold text-gray-800">FIBERFLOW TECHNOLOGIES INC.</h2>
                <p className="text-xs text-gray-600">Odiongan, Romblon, Philippines</p>
                <p className="text-xs text-gray-600">TIN: 670-207-920-00000</p>
                <p className="text-sm font-bold mt-2">PAYSLIP</p>
              </div>

              {/* Employee Information - DOLE Required */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-b border-gray-300 text-xs">
                <div className="p-3 border-r border-gray-300">
                  <p className="text-gray-500 uppercase text-[10px]">Employee Name</p>
                  <p className="font-bold">{selectedPayroll.employee_name || `${selectedPayroll.employee?.first_name} ${selectedPayroll.employee?.last_name}`}</p>
                </div>
                <div className="p-3 border-r border-gray-300">
                  <p className="text-gray-500 uppercase text-[10px]">Employee No.</p>
                  <p className="font-bold">{selectedPayroll.employee_no || selectedPayroll.employee?.employee_no}</p>
                </div>
                <div className="p-3 border-r border-gray-300">
                  <p className="text-gray-500 uppercase text-[10px]">Department</p>
                  <p className="font-bold">{selectedPayroll.employee?.department?.name || 'N/A'}</p>
                </div>
                <div className="p-3">
                  <p className="text-gray-500 uppercase text-[10px]">Position</p>
                  <p className="font-bold">{selectedPayroll.employee?.position?.title || 'N/A'}</p>
                </div>
              </div>

              {/* Pay Period Information - DOLE Required */}
              <div className="grid grid-cols-3 gap-0 border-b border-gray-300 bg-blue-50 text-xs">
                <div className="p-3 border-r border-gray-300">
                  <p className="text-gray-500 uppercase text-[10px]">Pay Period</p>
                  <p className="font-bold">{selectedPeriod ? `${formatDate(selectedPeriod.start_date)} - ${formatDate(selectedPeriod.end_date)}` : selectedPayroll.period_name}</p>
                </div>
                <div className="p-3 border-r border-gray-300">
                  <p className="text-gray-500 uppercase text-[10px]">Pay Date</p>
                  <p className="font-bold">{selectedPeriod ? formatDate(selectedPeriod.payment_date) : '-'}</p>
                </div>
                <div className="p-3">
                  <p className="text-gray-500 uppercase text-[10px]">Days Worked / Hours</p>
                  <p className="font-bold">{selectedPayroll.days_worked} days / {selectedPayroll.hours_worked || (parseFloat(selectedPayroll.days_worked) * 8).toFixed(2)} hrs</p>
                </div>
              </div>

              {/* Earnings & Deductions - DOLE Itemized */}
              <div className="grid grid-cols-2 gap-0">
                {/* Earnings Column */}
                <div className="border-r border-gray-800">
                  <div className="bg-gray-200 p-2 font-bold text-sm border-b border-gray-300">EARNINGS</div>
                  <div className="text-xs">
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Basic Pay</span>
                        <span className="block text-[10px] text-gray-500">({selectedPayroll.days_worked} days @ daily rate)</span>
                      </div>
                      <span className="font-medium">{formatCurrency(selectedPayroll.basic_pay || selectedPayroll.basic_salary)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Overtime Pay</span>
                        <span className="block text-[10px] text-gray-500">(125% of hourly rate - Art. 87, Labor Code)</span>
                      </div>
                      <span>{formatCurrency(selectedPayroll.overtime_pay)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Holiday Pay</span>
                        <span className="block text-[10px] text-gray-500">(Art. 94, Labor Code)</span>
                      </div>
                      <span>{formatCurrency(selectedPayroll.holiday_pay)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Night Shift Differential</span>
                        <span className="block text-[10px] text-gray-500">(10% premium - Art. 86)</span>
                      </div>
                      <span>{formatCurrency(selectedPayroll.night_differential)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Allowances</span>
                        <span className="block text-[10px] text-gray-500">(Non-taxable benefits)</span>
                      </div>
                      <span>{formatCurrency(selectedPayroll.allowances || selectedPayroll.total_allowances)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <span className="font-medium">Other Earnings</span>
                      <span>{formatCurrency(selectedPayroll.other_earnings)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-gray-100 font-bold">
                      <span>GROSS PAY</span>
                      <span>{formatCurrency(selectedPayroll.gross_pay)}</span>
                    </div>
                  </div>
                </div>

                {/* Deductions Column - With Legal Basis */}
                <div>
                  <div className="bg-gray-200 p-2 font-bold text-sm border-b border-gray-300">DEDUCTIONS</div>
                  <div className="text-xs">
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">SSS Contribution</span>
                        <span className="block text-[10px] text-gray-500">(RA 11199 - Social Security Act)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.sss_contribution)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">PhilHealth</span>
                        <span className="block text-[10px] text-gray-500">(RA 11223 - Universal Health Care)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.philhealth_contribution)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Pag-IBIG/HDMF</span>
                        <span className="block text-[10px] text-gray-500">(RA 9679 - Home Development)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.pagibig_contribution)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Withholding Tax</span>
                        <span className="block text-[10px] text-gray-500">(TRAIN Law - RA 10963)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.withholding_tax)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Late Deduction</span>
                        <span className="block text-[10px] text-gray-500">(Actual late hours × hourly rate)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.late_deduction)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <div>
                        <span className="font-medium">Loans</span>
                        <span className="block text-[10px] text-gray-500">(Authorized deduction)</span>
                      </div>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.loans)}</span>
                    </div>
                    <div className="flex justify-between p-2 border-b border-gray-100">
                      <span className="font-medium">CA/Other Deductions</span>
                      <span className="text-red-600">{formatCurrency(selectedPayroll.other_deductions)}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-red-50 font-bold text-red-700">
                      <span>TOTAL DEDUCTIONS</span>
                      <span>{formatCurrency(selectedPayroll.total_deductions)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Net Pay - Prominent Display */}
              <div className="bg-green-100 p-4 border-t-2 border-gray-800">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-lg font-bold text-gray-700">NET PAY</span>
                    <p className="text-[10px] text-gray-500">Amount to be received</p>
                  </div>
                  <span className="text-2xl font-bold text-green-700">{formatCurrency(selectedPayroll.net_pay)}</span>
                </div>
              </div>

              {/* DOLE Compliance Footer */}
              <div className="p-3 bg-gray-50 text-[10px] text-gray-600 border-t">
                <p className="font-bold">DOLE COMPLIANCE NOTICE (Labor Advisory No. 06-20):</p>
                <p>This payslip is issued in compliance with Article 103 of the Labor Code of the Philippines. Statutory deductions are made pursuant to applicable laws. Employee has the right to question any deduction within 10 days of receipt.</p>
                <p className="mt-2">For inquiries: HR Department | This is a computer-generated document.</p>
              </div>

              {/* Acknowledgment Section for Printing */}
              <div className="p-4 border-t border-gray-300 hidden print:block">
                <div className="flex justify-between mt-8">
                  <div className="text-center w-1/3">
                    <div className="border-t border-gray-400 mt-12 pt-2 mx-4">
                      <p className="text-xs">Employee Signature</p>
                      <p className="text-[10px] text-gray-500">Date Received: _____________</p>
                    </div>
                  </div>
                  <div className="text-center w-1/3">
                    <div className="border-t border-gray-400 mt-12 pt-2 mx-4">
                      <p className="text-xs">HR/Payroll Officer</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={handlePrintPayslip}>
            <PrinterIcon className="h-5 w-5 mr-2" />
            Print Payslip
          </Button>
          <Button variant="secondary" onClick={closePayslipModal}>
            Close
          </Button>
        </div>
      </Modal>
    </div>
  );
}
